import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

/**
 * ステップの種類
 * - external: この章の外のステップ（前後のStep）。破線ボーダー
 * - normal: メイン工程。青系
 * - check: 整合性チェックなど注意が必要な工程。オレンジ系
 * - complete: チェックリスト確認など完了に近い工程。緑系
 */
type StepVariant = 'external' | 'normal' | 'check' | 'complete';

type FlowStep = {
  /** ステップのタイトル */
  label: string;
  /** 補足説明（確認事項など） */
  sub?: string;
  /** ステップの種類（デフォルト: normal） */
  variant?: StepVariant;
};

/** 並列行の各列: 単一ステップ or 縦に連結する複数ステップ */
type FlowColumn = FlowStep | FlowStep[];

type FlowRow = FlowStep | FlowColumn[];

type Props = {
  /**
   * フローのステップ一覧。
   * - FlowStep: 単一カード
   * - FlowColumn[]: 横並び（並列）。各FlowColumnがFlowStep[]なら縦に連結される
   */
  steps: FlowRow[];
};

const VARIANT_STYLES: Record<
  StepVariant,
  {
    borderColor: string;
    borderStyle?: string;
    bgLight: string;
    bgDark: string;
    titleColor?: string;
  }
> = {
  external: {
    borderColor: 'text.disabled',
    borderStyle: 'dashed',
    bgLight: 'transparent',
    bgDark: 'transparent',
    titleColor: 'text.secondary',
  },
  normal: {
    borderColor: 'primary.main',
    bgLight: 'rgba(25, 118, 210, 0.04)',
    bgDark: 'rgba(25, 118, 210, 0.1)',
  },
  check: {
    borderColor: '#e65100',
    bgLight: 'rgba(230, 81, 0, 0.04)',
    bgDark: 'rgba(230, 81, 0, 0.1)',
  },
  complete: {
    borderColor: '#2e7d32',
    bgLight: 'rgba(46, 125, 50, 0.08)',
    bgDark: 'rgba(46, 125, 50, 0.15)',
    titleColor: '#2e7d32',
  },
};

const Arrow: React.FC = () => (
  <>
    <Box sx={{ width: 2, height: 20, bgcolor: 'text.disabled' }} />
    <Box
      sx={{
        width: 0,
        height: 0,
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTop: '6px solid',
        borderTopColor: 'text.disabled',
      }}
    />
  </>
);

const StepCard: React.FC<{ step: FlowStep; flex?: boolean }> = ({ step, flex }) => {
  const variant = step.variant ?? 'normal';
  const style = VARIANT_STYLES[variant];

  return (
    <Box
      sx={{
        ...(flex ? { flex: 1 } : { minWidth: variant === 'external' ? 200 : 320 }),
        mt: 1,
        px: variant === 'external' ? 3 : 2,
        py: step.sub ? 1.5 : 1,
        borderRadius: 2,
        border: 1,
        borderStyle: style.borderStyle ?? 'solid',
        borderColor: style.borderColor,
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? style.bgDark : style.bgLight),
        textAlign: 'center',
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ color: style.titleColor ?? 'text.primary' }}
      >
        {step.label}
      </Typography>
      {step.sub && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {step.sub}
        </Typography>
      )}
    </Box>
  );
};

/**
 * 手順フロー図（全体フロー）
 *
 * 縦方向に矢印でつなぐステップフロー。
 * steps に配列を渡すとその行は横並び（並列工程）になる。
 */
const WorkflowProcedureFlow: React.FC<Props> = ({ steps }) => {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 2.5,
        bgcolor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
      }}
    >
      <Stack spacing={0} alignItems="center">
        {steps.map((row, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <Arrow />}
            {Array.isArray(row) ? (
              <Stack
                direction="row"
                spacing={2}
                sx={{ mt: 1, width: '100%', alignItems: 'flex-start' }}
              >
                {row.map((col, sIdx) =>
                  Array.isArray(col) ? (
                    <Stack key={sIdx} alignItems="center" sx={{ flex: 1 }}>
                      {col.map((step, cIdx) => (
                        <React.Fragment key={cIdx}>
                          {cIdx > 0 && <Arrow />}
                          <StepCard step={step} flex />
                        </React.Fragment>
                      ))}
                    </Stack>
                  ) : (
                    <Stack key={sIdx} alignItems="center" sx={{ flex: 1 }}>
                      <StepCard step={col} flex />
                    </Stack>
                  ),
                )}
              </Stack>
            ) : (
              <StepCard step={row} />
            )}
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  );
};

export default WorkflowProcedureFlow;
